import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'

import { logger } from '@tribeplatform/react-components/common'

import { usePingSession } from './hooks/index.js'
import {
  ActorAnalyticsContext,
  SnowplowTracker,
  SnowplowTrackerFactory,
  TargetAnalyticsContext,
} from './lib/tracker/snowplow.js'
import { useRegisterSnowplowScript } from './lib/tracker/useRegisterSnowplowScript.js'

const isSSR = () => typeof window === 'undefined'

function getLang() {
  if (isSSR()) {
    return undefined
  }

  if (Array.isArray(navigator?.languages)) {
    return navigator.languages[0]
  }

  return navigator?.language
}

interface UnsetTrackerProps {
  actor?: Partial<ActorAnalyticsContext>
  target?: Partial<TargetAnalyticsContext>
}

const unsetContextValues = (
  context:
    | Partial<ActorAnalyticsContext>
    | Partial<TargetAnalyticsContext> = {},
) => {
  return Object.keys(context).reduce((acc, key: string) => {
    acc[key] = undefined
    return acc
  }, {})
}

export type TrackerClientConfig = {
  memberId?: string
  memberRoleId?: string
  memberRoleType?: string
  memberLocale?: string
  networkId?: string
  organizationId?: string
  appVersion?: string
  releaseChannel?: string
  snowplowCollectorAddress?: string
  enablePagePing?: boolean
  enablePageView?: boolean
}

export type TrackerClientContextType = {
  config: TrackerClientConfig
  unsetTracker: (props: UnsetTrackerProps) => void
  tracker: SnowplowTracker
}

export const TrackerClientContext = createContext<TrackerClientContextType>({
  config: null,
  unsetTracker: () => null,
  tracker: null,
})

export interface TrackerClientProviderProps {
  config: TrackerClientConfig
  children: ReactNode
}

export const TrackerClientProvider = ({
  config,
  children,
}: TrackerClientProviderProps) => {
  const {
    memberId,
    memberLocale,
    memberRoleId,
    memberRoleType,
    networkId,
    organizationId,
    enablePagePing,
    enablePageView,
    appVersion,
    releaseChannel,
    snowplowCollectorAddress,
  } = config ?? {}

  usePingSession({
    useQueryOptions: {
      enabled: !isSSR(),
      onError: error => {
        logger.warn('Ping session error', error)
      },
    },
  })

  const targetDefaultContext: TargetAnalyticsContext = useMemo(
    () => ({
      organizationId: organizationId || '',
      networkId: networkId || '',
    }),
    [networkId, organizationId],
  )

  const actorDefaultContext: ActorAnalyticsContext = useMemo(
    () => ({
      id: memberId || '',
      roleId: memberRoleId || '',
      roleType: memberRoleType || '',
      locale: memberLocale || getLang(),
    }),
    [memberId, memberLocale, memberRoleId, memberRoleType],
  )

  const tracker = useMemo(() => {
    return SnowplowTrackerFactory(
      {
        trackerName: 'tribe-analytics',
        enablePagePing: !!enablePagePing,
        enablePageView: !!enablePageView,
        appVersion,
        releaseChannel,
        snowplowCollectorAddress,
      },
      {
        actor: actorDefaultContext,
        target: targetDefaultContext,
      },
    )
  }, [
    actorDefaultContext,
    appVersion,
    enablePagePing,
    enablePageView,
    releaseChannel,
    snowplowCollectorAddress,
    targetDefaultContext,
  ])

  useRegisterSnowplowScript({ networkId, tracker })

  const unsetTracker = useCallback(
    ({ actor, target }: UnsetTrackerProps) => {
      tracker.setActor(unsetContextValues(actor))
      tracker.setTarget(unsetContextValues(target))
    },
    [tracker],
  )

  const value = useMemo(
    () => ({ config, unsetTracker, tracker }),
    [config, unsetTracker, tracker],
  )

  return (
    <TrackerClientContext.Provider value={value}>
      {children}
    </TrackerClientContext.Provider>
  )
}

export const useTrackerClient = () => {
  const context = useContext(TrackerClientContext)
  if (!context) {
    throw new Error(
      'useAnalyticsClient must be used within a AnalyticsClientProvider',
    )
  }
  return context
}
